"use client";

// The lower tail of the landing — everything after the everyone act, on the deep
// ground the cinematic left behind:
//
//   • Privacy — the stances read on the RIGHT while netti stands left-centre
//     (driven by the landing controller); hairline rows, no decorative marks,
//     every line true to SECURITY.md.
//   • Faq — light, hairline-only, one open at a time.
//   • FooterCta — the closing act: an interesting last line, one white begin
//     pill (netti mark in blue, ink in the deep brand blue), then the lockup +
//     only the links that matter.
//
// Calm, premium, scroll-revealed. Voice: civilizational, not feature-y. Lowercase
// everywhere (the type law) — no uppercase, no em-dashes.

import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion, springTransition, type Variants } from "@/lib/motion";
import { FAQS } from "@/lib/landing/faqs";
import SocialLinks from "../SocialLinks";
import ProductHuntBadge from "../ProductHuntBadge";
import { FOOTER_LINKS } from "../../lib/nav/siteNav";
import { CALENDLY } from "../../lib/publicLinks";
import ContextMenu, { useContextMenu } from "../os/ContextMenu";

// the two ways to reach us, in the menu's own line-glyph language
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const ContactIcons = {
  call: (<svg viewBox="0 0 24 24" {...stroke}><rect x="3.5" y="4.5" width="17" height="16" rx="2.4" /><path d="M3.5 9.5h17M8 3v3M16 3v3" /><circle cx="12" cy="14.5" r="1.6" /></svg>),
  mail: (<svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="5.5" width="18" height="13" rx="2.2" /><path d="M4 7l8 5.6L20 7" /></svg>),
};
import { Label } from "../Typography";
import { PadlockCell, CellIcon } from "./CellIcons";
import { NettiCta } from "./home/NettiCta";
import { LinkBar } from "../skills/PasteUrl";
import { useCopy } from "./home/bits";

// ── Reveal — the standard in-view entrance: a staggered blur-fade-rise. ──
const container: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } } };
const item: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(8px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: springTransition("settle") },
};
const itemReduced: Variants = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.2 } } };

export function Reveal({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.25, margin: "0px 0px -8% 0px" });
  return (
    <motion.div ref={ref} className={className} style={style} variants={reduce ? { hidden: {}, show: {} } : container} initial="hidden" animate={inView ? "show" : "hidden"}>
      {children}
    </motion.div>
  );
}
const MAP = { div: motion.div, h2: motion.h2, h3: motion.h3, p: motion.p, li: motion.li, span: motion.span } as const;
function RItem({ children, className, as = "div", style }: { children: React.ReactNode; className?: string; as?: keyof typeof MAP; style?: React.CSSProperties }) {
  const reduce = useReducedMotion() ?? false;
  const M = MAP[as];
  return <M className={className} style={style} variants={reduce ? itemReduced : item}>{children}</M>;
}

// ───────────────────────── privacy — centre stage, over the darkened organism ─────────────────────────
// Every line maps 1:1 to a shipped mechanism (SECURITY.md truth table). No claim
// we can't stand behind: disclosure classes are named as coming, not present;
// encryption at rest is opt-in, not "on by default"; no e2e / signed-commit talk.
// The 6 security stances. Keys align 1:1 with CellIcon symbols (rls · hash ·
// oauth · lock · gdpr · circle). The spiral field itself is rendered + driven by
// the landing controller in TestLanding.tsx (a fixed, scroll-scrubbed carousel
// around netti); this array is the shared source of truth for both.
export type Stance = { k: "rls" | "hash" | "oauth" | "lock" | "gdpr" | "circle"; t: string; d: string };
export const STANCES: Stance[] = [
  { k: "rls", t: "row-level security", d: "your rows answer only to you. private by default, enforced in the database, not just the app." },
  { k: "hash", t: "tokens, hashed", d: "access tokens are stored hashed. a database leak reveals nothing usable." },
  { k: "oauth", t: "oauth 2.1", d: "the same standard your tools already trust. no passwords ever pass through us." },
  { k: "lock", t: "encrypted at rest", d: "column-level encryption for the sensitive fields, opt-in per project. you're in control." },
  { k: "gdpr", t: "gdpr, honoured", d: "export it, delete it, on demand. it's your memory." },
  { k: "circle", t: "disclosure classes", d: "coming: a privacy class on every fact, so the most sensitive stays in its circle of trust." },
];
// The /test draft rewrites three sentences — the privacy lead, the honesty tail
// and the footer eyebrow/sub. Rather than fork the whole file, each takes a
// `copy` voice: "live" is what innernet.live says today, "explore" is the draft.
export type LandingCopy = "live" | "explore";

export function PrivacySection({ copy = "live" }: { copy?: LandingCopy }) {
  return (
    <section id="privacy" className="priv" aria-label="privacy">
      {/* netti takes centre stage behind the words (controller-driven, cipher
          veil, darkened by the scrim); the 6 stance cards spiral AROUND it as you
          scroll (the field lives in TestLanding). This copy crowns the top. */}
      <Reveal className="priv-in priv-in--head">
        <RItem className="priv-crown">
          <span className="priv-padlock" aria-hidden><PadlockCell size={58} doubleShackle={false} /></span>
        </RItem>
        <RItem><Label className="ls-kicker">it stays yours</Label></RItem>
        <RItem as="h2"><span className="priv-h">privacy</span></RItem>
        {/* the draft leads with the single most valuable sentence we own — word
            for word from the privacy policy, and the first question every
            developer asks. */}
        <RItem as="p" className="priv-lead">
          {copy === "explore"
            ? <>your memory is never for sale, and never anyone else&apos;s training data. it answers only to you — here is exactly how it&apos;s kept.</>
            : <>a memory of you should answer only to you. here is exactly how it&apos;s kept.</>}
        </RItem>
      </Reveal>

      {/* MOBILE — the 6 stances as a tight, swipeable scroll-snap rail (1:1),
          feathered at both edges. Desktop uses the drifting curved ticker in
          TestLanding (.tl-priv-field); this rail is display:none there. */}
      <div className="priv-rail" role="list" aria-label="how your memory is kept">
        {STANCES.map((st) => (
          <div key={st.k} className="priv-card priv-rail__card" role="listitem">
            <span className="priv-card__ic"><CellIcon symbol={st.k} size={40} /></span>
            <span className="priv-card__t">{st.t}</span>
            <span className="priv-card__d">{st.d}</span>
          </div>
        ))}
      </div>

      {/* the honesty message — NOT a Reveal: the landing controller owns its
          opacity + rise (phase 4), fading it in below where netti moved up to.
          id="priv-foot" is how the controller grabs it. */}
      <div id="priv-foot" className="priv-in priv-in--foot">
        {/* the honesty line buys more credibility than every adjective on the
            page. The draft cuts the tail claim — "nothing reads anything until
            you turn it on" oversold our own policy; match the policy, not the
            marketing. */}
        <p className="priv-honesty">
          one thing we won&apos;t pretend: once an ai you connect reads your memory, it&apos;s in that ai&apos;s hands. connect the ones you trust{copy === "explore" ? "." : ", and nothing reads anything until you turn it on."}
        </p>
        <div className="priv-links">
          <a href="/privacy">privacy policy</a>
          <span aria-hidden>·</span>
          <a href="/security">security</a>
          <span aria-hidden>·</span>
          <a href="/terms">terms</a>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── faq — light, one open at a time ─────────────────────────
// Content lives in lib/landing/faqs.ts — shared with the FAQPage JSON-LD the
// apex page emits, so the schema can never drift from what's on screen.
export function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="faq" aria-label="questions">
      {/* netti holds centre-left; the questions read left-aligned on the RIGHT
          hemisphere (controller-positioned). */}
      <Reveal className="faq-col">
        <RItem><Label className="ls-kicker">questions</Label></RItem>
        <RItem as="h2"><span className="faq-h">the short answers.</span></RItem>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <RItem key={f.q} className="faq-row" as="div">
              <button type="button" className="faq-q" data-open={open === i || undefined} onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
                <span>{f.q}</span>
                <svg className="faq-chev" width="13" height="13" viewBox="0 0 14 14" aria-hidden><path d="M3.5 5.5 7 9l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <div className="faq-a" data-open={open === i || undefined}><p>{f.a}</p></div>
            </RItem>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

// ───────────────────────── footer — the closing act ─────────────────────────
// Compact: the closing invitation, then ONE quiet row — social + only the links
// that matter. No repeated wordmark, no tagline gap. The link set is THE one
// list in lib/nav/siteNav (the ≡ menu footer reads the same one), so the two
// footers can never drift apart again — they did once, and one of them kept a
// dead link the other had dropped.

// THE MCP ENDPOINT IS THE CONNECTION, and this bar is how the page hands it over
// rather than describing it. There is nothing to download: one url, pasted into
// any tool that speaks mcp.
//
// ⚠️ DO NOT swap this for `npx innernet`. They are not two framings of one thing
// — the endpoint IS the connection, and the cli is a different wiring system
// that configures local clients to point at it. This bar closed the page on the
// endpoint for a reason; the cli belongs in the api card's fine print, where it
// is offered to someone wiring their own machine.
const MCP_ENDPOINT = "innernet.live/api/mcp";

/** THE link bar (components/skills/PasteUrl, law: one way a pasteable link is
 *  shown): the value in the house sans, the copy disc settling to a check. */
function CopyBar({ text, clip, label }: { text: string; clip?: string; label: string }) {
  const [done, copy] = useCopy(clip ?? text);
  return <LinkBar url={clip ?? `https://${text}`} shown={text} done={done} onCopy={copy} label={label} />;
}

function EndpointBar() {
  return <CopyBar text={MCP_ENDPOINT} clip={`https://${MCP_ENDPOINT}`} label="copy the link" />;
}

export function FooterCta({ signedIn = false, ctaRef, onBegin, copy = "live", bar = true, door = true }: {
  signedIn?: boolean;
  ctaRef: React.Ref<HTMLElement>;
  onBegin?: () => void;
  copy?: LandingCopy;
  /** the quiet link row under the close. Off where the page carries the
   *  defined footer (HomeFooter) after it instead. */
  bar?: boolean;
  /** the door under the endpoint. Off where the header's door stays lit through
   *  the close, so the page ends on ONE door (the live homepage, 2026-10-22). */
  door?: boolean;
}) {
  // the same menu the dashboard's share/options buttons open — one component,
  // so a public page and the app agree on what a small menu looks like
  const { menu, openAt, close } = useContextMenu();
  // ⚠️ anchored to the BUTTON, not to a point. Opened at a bare point the menu
  // was placed by the cursor-flip rule and could land under the pointer or away
  // from the control; anchored, it opens from the button's own edge — above it
  // here, since contact sits at the very bottom of the page — so it appears
  // exactly where you pressed and closes without moving the mouse.
  const openContact = (e: React.MouseEvent) => {
    openAt(
      e.currentTarget as HTMLElement,
      [
        { label: "book a meeting", icon: ContactIcons.call, onSelect: () => window.open(CALENDLY, "_blank", "noopener,noreferrer") },
        { label: "email us", icon: ContactIcons.mail, onSelect: () => window.location.assign("mailto:connect@innernet.live") },
      ],
    );
  };

  return (
    <footer ref={ctaRef} id="begin" className="fcta">
      {/* the organism crowns this space from the TOP-RIGHT (landing controller);
          the closing words rest in the BOTTOM-LEFT and the door in the
          BOTTOM-RIGHT — the corner the fixed top-right pill fades out of as this
          arrives, so the invitation travels down the page rather than doubling. */}
      <div className="fcta-crown">
        <Reveal className="fcta-crown__in">
          {/* the draft closes on the mechanic rather than the feeling — the
              same two beats the hero opened with. */}
          <RItem className="fcta-eyebrow">
            {copy === "explore" ? "one url. one sign-in." : "it's been waiting to meet you."}
          </RItem>
          <RItem as="h2" className="fcta-head">
            the rest is memory.
          </RItem>
          <RItem as="p" className="fcta-sub">
            {copy === "explore"
              ? "paste the endpoint, open a different tool, and it already knows you."
              : "any ai, any tool. always current, always yours. it starts the moment you begin."}
          </RItem>

        </Reveal>

        {/* the door, bottom-right: the endpoint you'd paste, then the way in.
            Handing over the url IS the install instruction — there is nothing to
            download, and showing that beats claiming it. */}
        <Reveal className="fcta-act">
          <RItem as="div" className="fcta-endpoint-wrap">
            <span className="fcta-endpoint__lead">paste this into any ai you already talk to</span>
            <EndpointBar />
          </RItem>

          {door && <RItem as="div" className="fcta-begin">
            {/* THE door, the same one as the hero's and the header's: the solid
                pill, netti's gradient mark in the white disc (DoorMark) */}
            {signedIn ? (
              <NettiCta label="enter" href="/dashboard" ariaLabel="open your innernet dashboard" />
            ) : (
              <NettiCta label="create yours" onClick={onBegin} ariaLabel="create your innernet" />
            )}
            {/* a signed-in person needs no reassurance line under their own door */}
            {!signedIn && <span className="fcta-begin__note">free to start · no card</span>}
          </RItem>}
        </Reveal>
      </div>

      {/* one quiet row — links · launch badge · social. NO wordmark here: only the
          top breadcrumb menu says innernet. */}
      {bar && <Reveal className="fcta-bar">
        <nav className="fcta-bar__links" aria-label="footer">
          {/* ONE door to a person, and it behaves like the rest of the product:
              plain text (no pill, no border) that opens the app's own options
              menu with the two ways through — book a meeting, or write. The
              separate "contact" mailto and the "talk to us" pill were the same
              offer twice, in two shapes. */}
          <button type="button" className="fcta-bar__contact os-link" onClick={openContact}>
            <span className="os-link-text">contact</span>
            {/* the dashboard dock's own chevron — the mark that says "this opens" */}
            <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden>
              <path d="M3.25 5.1 6.5 8.35 9.75 5.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {FOOTER_LINKS.filter(([label]) => label !== "contact").map(([label, href]) => (
            <a key={label} href={href}>{label}</a>
          ))}
        </nav>
        <div className="fcta-bar__badges">
          <span className="fcta-bar__ph"><ProductHuntBadge theme="light" width={210} /></span>
          <span className="fcta-bar__social"><SocialLinks /></span>
        </div>
      </Reveal>}
      <ContextMenu menu={menu} onClose={close} />
    </footer>
  );
}

// convenience: the whole tail in order. the footer gets the ref for the
// organism's return + the onBegin that opens the seamless flow.
export default function LandingSections({ signedIn, ctaRef, onBegin, copy = "live" }: { signedIn: boolean; ctaRef: React.Ref<HTMLElement>; onBegin: () => void; copy?: LandingCopy }) {
  return (
    <>
      <PrivacySection copy={copy} />
      <FaqSection />
      <FooterCta signedIn={signedIn} ctaRef={ctaRef} onBegin={onBegin} copy={copy} />
    </>
  );
}
